import React, { useEffect, useState, useRef } from 'react';
import { NorthIcon, ColorizeOutlinedIcon } from 'icons/materialIcons';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Checkbox,
    Grid,
    FormControlLabel,
    FormControl,
    Button
} from '@mui/material';
import { ChromePicker } from 'react-color';

const TeamV2VirtualBackgroundContainer = ({ memberData, setMemberData, isNewMember = false }) => {
    const [selectedButton, setSelectedButton] = useState('library');
    const [refreshKey, setRefreshKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const colorOptions = ['#000000', '#eb5757', '#f2994b', '#f2c84b', '#219654', '#2f82ed', '#9b51e0'];
    const [showQRCodeColorPicker, setShowQRCodeColorPicker] = useState(false);
    const cardPickerRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [imagesPerPage] = useState(12);
    const [qrCodeColor, setQrCodeColor] = useState('#000000');
    const [images, setImages] = useState([]);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [showCropper, setShowCropper] = useState(false);

    // Mock virtual background data
    const [virtualBackgroundData, setVirtualBackgroundData] = useState({
        cardName: memberData.firstName && memberData.lastName ? `${memberData.firstName} ${memberData.lastName}` : '',
        company: memberData.company || '',
        jobTitle: memberData.jobTitle || '',
        location: memberData.location || '',
        qrCodeColor: '#000000',
        showQRCode: true,
        condensedView: false,
        qrCodeLogoUrl: null,
        backgroundImageUrl: null,
        showCardName: true,
        showCompany: true,
        showJobTitle: true,
        showLocation: true
    });

    // Mock images for library
    useEffect(() => {
        const mockImages = Array.from({ length: 24 }, (_, i) => ({
            id: i + 1,
            webformatURL: `https://picsum.photos/400/300?random=${i + 1}`,
            tags: `background-${i + 1}`
        }));
        setImages(mockImages);
    }, []);

    const handleCancel = () => {
        setVirtualBackgroundData(prev => ({
            ...prev,
            qrCodeColor: '#000000',
            qrCodeLogoUrl: null
        }));
        setRefreshKey(prev => prev + 1);
    };

    const handleImageClick = (url) => {
        setVirtualBackgroundData(prev => ({
            ...prev,
            backgroundImageUrl: url
        }));
    };

    const handleQRCodeColorPickertoggle = () => {
        setShowQRCodeColorPicker((prev) => !prev);
    };

    const handleQRCodeColorChangeComplete = (color) => {
        setQrCodeColor(color.hex);
        setVirtualBackgroundData(prev => ({
            ...prev,
            qrCodeColor: color.hex
        }));
    };

    // Handle outside clicks to close the color picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cardPickerRef.current && !cardPickerRef.current.contains(event.target)) {
                setShowQRCodeColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Pagination logic
    const indexOfLastImage = currentPage * imagesPerPage;
    const indexOfFirstImage = indexOfLastImage - imagesPerPage;
    const currentImages = images.slice(indexOfFirstImage, indexOfLastImage);

    const nextPage = () => {
        if (currentPage < Math.ceil(images.length / imagesPerPage)) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handleCheckboxChange = (field, checked) => {
        setVirtualBackgroundData(prev => ({
            ...prev,
            [field]: checked
        }));
    };

    return (
        <Container maxWidth="sm">
            <Box my={4}>
                <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">
                    Virtual background
                </Typography>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <Box sx={{ marginTop: 5 }}>
                        {/* QR Code Color Picker */}
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '13px' }}>
                            Choose QR color
                        </Typography>
                        <Box
                            sx={{
                                marginBottom: 2,
                                width: '60%',
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <IconButton
                                    onClick={handleQRCodeColorPickertoggle}
                                    sx={{
                                        position: 'relative',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            border: `2px solid ${qrCodeColor}`,
                                        }}
                                    />
                                    <ColorizeOutlinedIcon sx={{ color: '#828282', fontSize: 16 }} />
                                </IconButton>

                                {colorOptions.map((color, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => handleQRCodeColorChangeComplete({ hex: color })}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            position: 'relative',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                border: `2px solid ${qrCodeColor === color ? '#dbdbdb' : '#ffffff'}`,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                width: '85%',
                                                height: '85%',
                                                borderRadius: '50%',
                                                backgroundColor: color,
                                            }}
                                        />
                                    </Box>
                                ))}

                                {showQRCodeColorPicker && (
                                    <Box sx={{ position: 'absolute', top: '40px', right: 0, zIndex: 2 }} ref={cardPickerRef}>
                                        <ChromePicker color={qrCodeColor} onChange={handleQRCodeColorChangeComplete} disableAlpha />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* QR Code Logo Component Placeholder */}
                        <Box sx={{ marginTop: 4 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '120px',
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                    QR Code Logo Upload Area
                                </Typography>
                            </Box>
                        </Box>

                        {/* Checkboxes Container */}
                        <Box sx={{ marginTop: 4 }}>
                            <FormControl component="fieldset" variant="standard">
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={virtualBackgroundData.condensedView}
                                                    onChange={(event) => handleCheckboxChange('condensedView', event.target.checked)}
                                                    color="dark"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Condensed View
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#828282' }}>
                                                        Optimal for Microsoft Teams
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={virtualBackgroundData.showQRCode}
                                                    onChange={(event) => handleCheckboxChange('showQRCode', event.target.checked)}
                                                    color="dark"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        QR Code
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#828282' }}>
                                                        QR Code can be customized in the QR Code section
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>

                                    {/* Name and Company Checkboxes in Row */}
                                    <Grid container item spacing={1}>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={virtualBackgroundData.showCardName}
                                                        onChange={(event) => handleCheckboxChange('showCardName', event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Name
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={virtualBackgroundData.showCompany}
                                                        onChange={(event) => handleCheckboxChange('showCompany', event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Company
                                                    </Typography>
                                                }
                                            />
                                        </Grid>

                                        {/* Job Title and Location Checkboxes in Row */}
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={virtualBackgroundData.showJobTitle}
                                                        onChange={(event) => handleCheckboxChange('showJobTitle', event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Job Title
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={virtualBackgroundData.showLocation}
                                                        onChange={(event) => handleCheckboxChange('showLocation', event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Location
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Box>

                        {/* Library/Upload Toggle */}
                        <Box sx={{ backgroundColor: '#f7f7f7', padding: '8px', borderRadius: '8px', marginTop: 4 }}>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="text"
                                    onClick={() => setSelectedButton('library')}
                                    sx={{
                                        flexGrow: 1,
                                        borderRadius: '8px',
                                        backgroundColor: selectedButton === 'library' ? 'white' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        '&:active': {
                                            backgroundColor: 'white',
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                            backgroundColor: 'white',
                                        },
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: "#4f4f4f",
                                        textTransform: 'none',
                                    }}
                                >
                                    Choose from Library
                                </Button>

                                <Button
                                    variant="text"
                                    onClick={() => setSelectedButton('upload')}
                                    sx={{
                                        flexGrow: 1,
                                        borderRadius: '8px',
                                        backgroundColor: selectedButton === 'upload' ? 'white' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        '&:active': {
                                            backgroundColor: 'white',
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                            backgroundColor: 'white',
                                        },
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: "#4f4f4f",
                                        textTransform: 'none',
                                    }}
                                >
                                    Upload Image
                                </Button>
                            </Box>
                        </Box>

                        {selectedButton === 'library' && (
                            <>
                                {/* Image Grid Display */}
                                <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ marginTop: "2px" }}>
                                    {currentImages.map((image, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: 0,
                                                    paddingBottom: '75%',
                                                    position: 'relative',
                                                    borderRadius: 3,
                                                    border: virtualBackgroundData.backgroundImageUrl === image.webformatURL ? '4px solid #2fabed' : 'none',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleImageClick(image.webformatURL)}
                                            >
                                                <img
                                                    src={image.webformatURL}
                                                    alt={image.tags}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: 8,
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Pagination Buttons */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 5 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={prevPage}
                                        disabled={currentPage === 1}
                                        startIcon={<NorthIcon sx={{ transform: 'rotate(-90deg)' }} />}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: '#4f4f4f',
                                            textTransform: 'none',
                                            padding: '6px 28px',
                                            borderRadius: '18px',
                                            borderColor: currentPage === 1 ? '#e0e0e0' : '#dfdfdf',
                                        }}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={nextPage}
                                        disabled={currentPage >= Math.ceil(images.length / imagesPerPage)}
                                        endIcon={<NorthIcon sx={{ transform: 'rotate(90deg)' }} />}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: '#4f4f4f',
                                            borderRadius: '18px',
                                            padding: '6px 28px',
                                            textTransform: 'none',
                                            borderColor: currentPage >= Math.ceil(images.length / imagesPerPage) ? '#e0e0e0' : '#dfdfdf',
                                        }}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            </>
                        )}

                        {selectedButton === 'upload' && (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '200px',
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer',
                                    marginTop: 2
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                    Upload Image Area (Coming Soon)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default TeamV2VirtualBackgroundContainer;
